/**
 * OfficerRadioCard — officer pool radios (102-117 range etc.)
 *
 * Two indicators:
 *   1. Available  — green dot when radio is sitting in the rack.
 *                   Turns BLUE + officer's last name when checked out
 *                   (radio is on duty with that officer).
 *                   Click to return radio to rack (clear assignment).
 *
 *   2. Not Avail. — red dot, same as standard RadioCard.
 *                   Click to prompt for last holder and mark unavailable.
 *
 * Border follows state: green (available) → blue (assigned/on duty) →
 * red (not available) → pink (OOS).
 */
import { Card, StatusDot, dotLabelColor } from '../ui'
import { WALKIE_ICON } from '../../assets'
import { markDirty } from '../../stores/sync'
import type { RadioSlot } from '../../stores/desktop'

interface OfficerRadioCardProps {
  radio: RadioSlot
  onUpdate: (patch: Partial<RadioSlot>) => void
}

/** Take the last word of a name, stripping any "(nickname)" suffix. */
function lastName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function OfficerRadioCard({ radio, onUpdate }: OfficerRadioCardProps) {
  const isOos      = !!radio.oos
  const isAssigned = !isOos && !radio.available && !!radio.assignedTo   // on duty with an officer
  const isAvail    = !isOos && radio.available && !radio.assignedTo     // sitting in rack
  const isNotAvail = !isOos && !radio.available && !radio.assignedTo    // missing / not available

  const borderColor = isOos       ? '#fca5a5'
                    : isAssigned  ? '#3b82f6'   // blue — on duty
                    : isNotAvail  ? '#f87171'   // red
                    : '#4ade80'                 // green — available

  const imgActive = isAssigned || isAvail

  /** Click Available row: return radio to rack. */
  function handleAvailable() {
    if (isOos) return
    markDirty()
    onUpdate({ available: true, assignedTo: '' })
  }

  /** Click Not Available row: prompt for officer name and check out the radio. */
  function handleNotAvail() {
    if (isOos) return
    markDirty()
    if (isAvail || isAssigned) {
      const name = window.prompt(`Radio ${radio.id} — Officer's name?`) || ''
      if (name.trim()) {
        onUpdate({ available: false, assignedTo: name.trim() })
      } else {
        onUpdate({ available: false, assignedTo: '' })
      }
    } else {
      // Already not available — click again to clear back to available
      onUpdate({ available: true, assignedTo: '' })
    }
  }

  return (
    <Card variant="item" borderColor={borderColor}>
      {/* Radio ID label */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--text-muted)',
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        Radio {radio.id}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {/* Walkie icon */}
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img
            src={WALKIE_ICON}
            alt={`Radio ${radio.id}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: imgActive ? 1 : 0.4,
              filter:  imgActive ? 'none' : 'grayscale(100%)',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 4,
            flex: 1,
            minWidth: 0,
          }}
        >
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Row 1 — Available / On Duty */}
              <div
                onClick={handleAvailable}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: isAssigned || isNotAvail ? 'pointer' : 'default',
                  opacity: isAvail || isAssigned ? 1 : 0.3,
                }}
              >
                <StatusDot
                  variant={isAssigned ? 'blue' : 'green'}
                  active={isAvail || isAssigned}
                />
                {isAssigned ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#1d4ed8',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lastName(radio.assignedTo)}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: dotLabelColor('green', isAvail),
                    }}
                  >
                    Available
                  </span>
                )}
              </div>

              {/* Row 2 — Not Available */}
              <div
                onClick={handleNotAvail}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  opacity: isNotAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: dotLabelColor('red', isNotAvail),
                  }}
                >
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
